/**
 * PricingSurchargesPage — `/settings/surcharges`.
 *
 * Full CRUD surface on top of service-pricing for the pricing_surcharge table:
 *
 *   - GET    /v1/pricing/surcharges/admin     — list (active + inactive)
 *   - POST   /v1/pricing/surcharges           — create
 *   - PATCH  /v1/pricing/surcharges/:id       — update
 *   - DELETE /v1/pricing/surcharges/:id       — hard-delete (no FK refs in schema)
 *
 * Mirrors PricingZonesPage line-for-line: page header with "New surcharge"
 * button, table with per-row Edit + Delete, modal for create / edit, confirm
 * delete dialog.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Percent, Pencil, Plus, Trash2, X } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  createSurcharge,
  deleteSurcharge,
  listSurchargesAdmin,
  type CreateSurchargeInput,
  type PricingSurchargeAdmin,
  updateSurcharge,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

const SURCHARGES_ADMIN_KEY = ['pricing', 'surcharges', 'admin'] as const;

/** Empty form template — used when opening "New surcharge". */
function blankDraft(): CreateSurchargeInput {
  // Default validFrom = today at 00:00 UTC, ISO-8601.
  const today = new Date();
  const validFrom = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  ).toISOString();
  return {
    code: '',
    name: '',
    kind: 'FUEL',
    amount: '0.00',
    isPercentage: false,
    active: true,
    validFrom,
    validUntil: null,
  };
}

function rowToDraft(s: PricingSurchargeAdmin): CreateSurchargeInput {
  return {
    code: s.code,
    name: s.name,
    kind: s.kind,
    amount: s.amount,
    isPercentage: s.isPercentage,
    active: s.active,
    validFrom: s.validFrom,
    validUntil: s.validUntil,
  };
}

/** Extract the server-side message from an ApiError body, falling back. */
function extractServerMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    if (body && typeof body.message === 'string' && body.message.length > 0) {
      return body.message;
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function PricingSurchargesPage(): JSX.Element {
  const qc = useQueryClient();

  const surcharges = useQuery({
    queryKey: SURCHARGES_ADMIN_KEY,
    queryFn: () => listSurchargesAdmin(),
    retry: false,
    staleTime: 60_000,
  });

  const denied =
    surcharges.isError &&
    surcharges.error instanceof ApiError &&
    (surcharges.error.status === 401 || surcharges.error.status === 403);

  const [modal, setModal] = useState<
    | null
    | { mode: 'create'; draft: CreateSurchargeInput }
    | { mode: 'edit'; id: string; draft: CreateSurchargeInput }
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<PricingSurchargeAdmin | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (input: CreateSurchargeInput) => createSurcharge(input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: SURCHARGES_ADMIN_KEY });
      setModal(null);
      setModalError(null);
    },
    onError: (e) => setModalError(extractServerMessage(e, 'Create failed')),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; patch: Partial<CreateSurchargeInput> }) =>
      updateSurcharge(args.id, args.patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: SURCHARGES_ADMIN_KEY });
      setModal(null);
      setModalError(null);
    },
    onError: (e) => setModalError(extractServerMessage(e, 'Update failed')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSurcharge(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: SURCHARGES_ADMIN_KEY });
      setConfirmDelete(null);
      setDeleteError(null);
    },
    onError: (e) => {
      // The server's 409 (if it ever fires) would be surfaced verbatim — the
      // current PricingSurcharge schema has no FK references so the controller
      // hard-deletes, but a future schema change could re-introduce a 409.
      setDeleteError(extractServerMessage(e, 'Delete failed'));
    },
  });

  function submitModal(): void {
    if (!modal) return;
    if (modal.mode === 'create') {
      createMut.mutate(modal.draft);
    } else {
      updateMut.mutate({ id: modal.id, patch: modal.draft });
    }
  }

  const rows = surcharges.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pricing surcharges"
        description="Fixed + percentage surcharges layered on top of tariff base prices (e.g. fuel, holiday, hazmat)."
        actions={
          <div className="flex items-center gap-2">
            <Percent className="w-4 h-4 text-muted" />
            <button
              type="button"
              onClick={() => {
                setModalError(null);
                setModal({ mode: 'create', draft: blankDraft() });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> New surcharge
            </button>
          </div>
        }
      />

      {surcharges.isPending ? (
        <p className="text-sm text-muted">Loading surcharges…</p>
      ) : denied ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <p className="font-semibold">Permission required</p>
          <p>
            The token does not carry{' '}
            <code className="font-mono">Settings/managePricingConfig</code>.
          </p>
        </div>
      ) : surcharges.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          <p className="font-semibold">Pricing service unreachable</p>
          <p>{(surcharges.error as Error).message}</p>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No pricing surcharges found.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Code</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Name</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Kind</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Amount</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Type</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Active</th>
                <th className="px-4 py-2 text-right text-[10px] uppercase text-muted">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono">{s.code}</td>
                  <td className="px-4 py-2 text-xs">{s.name}</td>
                  <td className="px-4 py-2 text-xs font-mono">{s.kind}</td>
                  <td className="px-4 py-2 text-xs font-mono">{s.amount}</td>
                  <td className="px-4 py-2 text-xs">
                    {s.isPercentage ? 'Percentage' : 'Flat'}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {s.active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      aria-label={`Edit surcharge ${s.code}`}
                      onClick={() => {
                        setModalError(null);
                        setModal({ mode: 'edit', id: s.id, draft: rowToDraft(s) });
                      }}
                      className="text-gray-600 hover:bg-gray-100 rounded p-1 mr-1"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete surcharge ${s.code}`}
                      onClick={() => {
                        setDeleteError(null);
                        setConfirmDelete(s);
                      }}
                      className="text-red-600 hover:bg-red-50 rounded p-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal ? (
        <SurchargeModal
          mode={modal.mode}
          draft={modal.draft}
          onChange={(next) => setModal((m) => (m ? { ...m, draft: next } : m))}
          onCancel={() => {
            setModal(null);
            setModalError(null);
          }}
          onSubmit={submitModal}
          error={modalError}
          submitting={createMut.isPending || updateMut.isPending}
        />
      ) : null}

      {confirmDelete ? (
        <ConfirmDeleteModal
          surcharge={confirmDelete}
          onCancel={() => {
            setConfirmDelete(null);
            setDeleteError(null);
          }}
          onConfirm={() => deleteMut.mutate(confirmDelete.id)}
          submitting={deleteMut.isPending}
          error={deleteError}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

interface SurchargeModalProps {
  readonly mode: 'create' | 'edit';
  readonly draft: CreateSurchargeInput;
  readonly onChange: (next: CreateSurchargeInput) => void;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
  readonly error: string | null;
  readonly submitting: boolean;
}

function SurchargeModal(props: SurchargeModalProps): JSX.Element {
  const { draft, onChange } = props;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <p className="text-base font-bold text-gray-900">
            {props.mode === 'create' ? 'New surcharge' : 'Edit surcharge'}
          </p>
          <button
            type="button"
            onClick={props.onCancel}
            className="p-1.5 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label htmlFor="surcharge-code" className="text-[10px] font-semibold uppercase text-muted">
              Code
            </label>
            <input
              id="surcharge-code"
              type="text"
              aria-label="Code"
              value={draft.code}
              disabled={props.mode === 'edit'}
              onChange={(e) => onChange({ ...draft, code: e.target.value.toUpperCase() })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono uppercase disabled:bg-gray-100"
              placeholder="FUEL_2026Q2"
            />
            <p className="text-[10px] text-muted mt-1">
              Uppercase A–Z, 0–9, underscores. Cannot be edited after creation.
            </p>
          </div>
          <div className="col-span-2">
            <label htmlFor="surcharge-name" className="text-[10px] font-semibold uppercase text-muted">
              Name
            </label>
            <input
              id="surcharge-name"
              type="text"
              aria-label="Name"
              value={draft.name}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg"
              placeholder="Q2 2026 fuel surcharge"
            />
          </div>
          <div>
            <label htmlFor="surcharge-kind" className="text-[10px] font-semibold uppercase text-muted">
              Kind
            </label>
            <select
              id="surcharge-kind"
              aria-label="Kind"
              value={draft.kind}
              onChange={(e) => onChange({ ...draft, kind: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono"
            >
              <option value="FUEL">FUEL</option>
              <option value="HOLIDAY">HOLIDAY</option>
              <option value="HAZMAT">HAZMAT</option>
              <option value="DECLARED_VALUE_INSURANCE">DECLARED_VALUE_INSURANCE</option>
            </select>
          </div>
          <div>
            <label htmlFor="surcharge-amount" className="text-[10px] font-semibold uppercase text-muted">
              Amount
            </label>
            <input
              id="surcharge-amount"
              type="text"
              aria-label="Amount"
              value={draft.amount}
              onChange={(e) => onChange({ ...draft, amount: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg font-mono"
              placeholder={draft.isPercentage ? '0.05' : '500.00'}
            />
            <p className="text-[10px] text-muted mt-1">
              {draft.isPercentage ? 'Fraction (e.g. 0.05 = 5%).' : 'Decimal NGN (e.g. 500.00).'}
            </p>
          </div>
          <div>
            <label className="inline-flex items-center gap-2 text-sm mt-5">
              <input
                type="checkbox"
                aria-label="Is percentage"
                checked={!!draft.isPercentage}
                onChange={(e) => onChange({ ...draft, isPercentage: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Percentage</span>
            </label>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label="Active"
                checked={draft.active}
                onChange={(e) => onChange({ ...draft, active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Active</span>
            </label>
          </div>
        </div>
        {props.error ? (
          <div className="px-4 pb-2 text-xs text-red-600" role="alert">
            {props.error}
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={props.onCancel}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onSubmit}
            disabled={props.submitting}
            className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg disabled:opacity-60"
          >
            {props.submitting
              ? 'Saving…'
              : props.mode === 'create'
              ? 'Create surcharge'
              : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConfirmDeleteModalProps {
  readonly surcharge: PricingSurchargeAdmin;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly submitting: boolean;
  readonly error: string | null;
}

function ConfirmDeleteModal(props: ConfirmDeleteModalProps): JSX.Element {
  const s = props.surcharge;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
        <div className="p-4 border-b border-border">
          <p className="text-base font-bold text-gray-900">Confirm deletion</p>
        </div>
        <div className="p-4 text-sm text-gray-700 space-y-2">
          <p>
            Delete pricing surcharge <span className="font-mono">{s.code}</span> ({s.name})?
          </p>
          <p className="text-xs text-muted">
            This action is logged via the FinancialAudit trail. If you want to retire a
            surcharge without losing the row, soft-disable it (set Active off) instead.
          </p>
        </div>
        {props.error ? (
          <div className="px-4 pb-2 text-xs text-red-600" role="alert">
            {props.error}
          </div>
        ) : null}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={props.onCancel}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            disabled={props.submitting}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg disabled:opacity-60"
          >
            {props.submitting ? 'Deleting…' : 'Delete surcharge'}
          </button>
        </div>
      </div>
    </div>
  );
}
